import { API_BASE_URL } from "./api";

/** Portal library label with the original upload extension when the display name omits it. */
export function portalDownloadFilename(file) {
  const display = (file?.name || "").trim();
  const original = (file?.original_filename || "").trim();
  if (!display) return original || "download";
  if (!original) return display;
  const extMatch = original.match(/(\.[^./\\]+)$/i);
  const ext = extMatch ? extMatch[1] : "";
  if (ext && !display.toLowerCase().endsWith(ext.toLowerCase())) {
    return `${display}${ext}`;
  }
  return display;
}

export const uploadedFileName = (file) => portalDownloadFilename(file);

export function portalFileItemDownloadUrl(fileId, _accessToken) {
  // Auth via httpOnly cookie + credentials; never put JWTs in query strings.
  const id = encodeURIComponent((fileId || "").trim());
  return `${API_BASE_URL}/portal/files/item/${id}/download`;
}

/**
 * Parse filename from Content-Disposition (inline or attachment), including RFC 5987 filename*.
 */
export function filenameFromContentDisposition(headerValue) {
  if (!headerValue || typeof headerValue !== "string") return "";
  const v = headerValue.trim();
  const utf8Star = /filename\*\s*=\s*UTF-8''([^;\s]+)/i.exec(v);
  if (utf8Star?.[1]) {
    const raw = utf8Star[1].trim();
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  const quoted = /filename\s*=\s*"((?:\\.|[^"\\])*)"/i.exec(v);
  if (quoted?.[1]) return quoted[1].replace(/\\"/g, '"').replace(/\\\\/g, "\\");
  const unquoted = /filename\s*=\s*([^;\s]+)/i.exec(v);
  if (unquoted?.[1]) return unquoted[1].replace(/^"+|"+$/g, "");
  return "";
}

/** Read Content-Disposition safely from axios / fetch header objects. */
export function contentDispositionFromHeaders(headers) {
  if (!headers) return "";
  if (typeof headers.get === "function") {
    const g = headers.get("content-disposition") || headers.get("Content-Disposition");
    if (g) return String(g);
  }
  const h = headers["content-disposition"] ?? headers["Content-Disposition"];
  return h != null ? String(h) : "";
}

export const downloadFilenameFromHeaders = (headers, fallback) => {
  const fromDisp = filenameFromContentDisposition(contentDispositionFromHeaders(headers)).trim();
  if (fromDisp) return fromDisp;
  const fb = (fallback || "").trim();
  return fb || "download";
};

export const resolveUploadedContentType = (file, headerType) => {
  const header = String(headerType || file?.content_type || "").split(";")[0].trim().toLowerCase();
  if (header && header !== "application/octet-stream") return header;
  const name = uploadedFileName(file).toLowerCase();
  if (name.endsWith(".pdf")) return "application/pdf";
  if (name.endsWith(".doc")) return "application/msword";
  if (name.endsWith(".docx")) return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (name.endsWith(".ppt")) return "application/vnd.ms-powerpoint";
  if (name.endsWith(".pptx")) return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
  if (name.endsWith(".xls")) return "application/vnd.ms-excel";
  if (name.endsWith(".xlsx")) return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (name.endsWith(".txt")) return "text/plain";
  return header || "application/octet-stream";
};

export const shouldDownloadUploadedFile = (file, mime) => {
  if (mime === "application/pdf") return false;
  const name = uploadedFileName(file).toLowerCase();
  if (/\.(docx?|pptx?|xlsx?|rtf|pages|key|numbers)$/i.test(name)) return true;
  return mime.startsWith("application/vnd.") || mime.includes("officedocument");
};

import { portalPath } from "./portalPaths";
import { seedSessionTokenIntoWindow } from "./sessionToken";

export function portalFileViewPath(file, client) {
  const id = (file?.id || "").trim();
  if (!id) return "";
  const name = uploadedFileName(file).trim() || "download";
  if (!client) {
    return `/portal/files/view/${encodeURIComponent(id)}/${encodeURIComponent(name)}`;
  }
  return portalPath(client, `files/view/${encodeURIComponent(id)}/${encodeURIComponent(name)}`);
}

/** In-app HTML viewer for Library scopes — clean workspace URL `/{client}/files/scope`. */
export function portalFilePacketPath(file, client) {
  if (!isScopeLibraryFile(file)) {
    const id = (file?.id || file?.file_id || "").trim();
    if (!id) return "";
    if (!client) return `/portal/files/packet/${encodeURIComponent(id)}`;
    return portalPath(client, `files/packet/${encodeURIComponent(id)}`);
  }
  if (!client) return "/portal/files/scope";
  return portalPath(client, "files/scope");
}

export function isScopeLibraryFile(file) {
  const kind = String(file?.packet_kind || file?.kind || "").toLowerCase();
  if (kind.startsWith("scope")) return true;
  const name = String(file?.name || file?.file_name || "");
  const note = String(file?.note || "");
  return /scope/i.test(name) || /scope\s*\+\s*invoice/i.test(note);
}

/**
 * Open an in-app path in a new tab while keeping the tab session JWT when needed.
 * Always open about:blank first, seed the JWT, then navigate.
 *
 * Do not use window.open(url, "_blank", "noopener") — Chrome returns null even when the
 * tab opens, which previously also navigated the current tab (double open). Studio
 * agreement preview (/agreement/preview/…) also needs the seeded admin JWT; without it
 * the new tab falls through to the public partner view and shows "Agreement not found".
 */
export function openAppPathInNewTab(path) {
  if (!path || typeof window === "undefined") return false;
  const url = new URL(path, window.location.origin).href;
  const w = window.open("about:blank", "_blank");
  if (!w) {
    window.location.assign(url);
    return false;
  }
  seedSessionTokenIntoWindow(w);
  try {
    w.opener = null;
  } catch {
    // ignore
  }
  w.location.href = url;
  return true;
}

function looksLikePdf(file, mime) {
  const mimeLower = String(mime || "").toLowerCase();
  if (mimeLower === "application/pdf") return true;
  return /\.pdf$/i.test(uploadedFileName(file));
}

/** True when the library row is already known to be a PDF (before fetching). */
export function isLikelyPdfLibraryFile(file) {
  if (String(file?.file_type || "").toLowerCase() === "pdf") return true;
  if (/\.pdf$/i.test(uploadedFileName(file))) return true;
  if (/\.pdf$/i.test(String(file?.original_filename || ""))) return true;
  if (/\.pdf$/i.test(String(file?.url || ""))) return true;
  return false;
}

function closeWindowQuietly(win) {
  if (!win || win.closed) return;
  try {
    win.close();
  } catch {
    // ignore
  }
}

/** Download (or iOS Quick Look) a blob from the current document — no about:blank tab. */
function triggerBlobDownload(blob, filename, mime) {
  const typed =
    blob.type && blob.type !== "application/octet-stream"
      ? blob
      : new Blob([blob], { type: mime || "application/octet-stream" });
  const blobUrl = URL.createObjectURL(typed);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename || "download";
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

/**
 * Open a stored library file for preview/download.
 * - Scope packets: HTML in-app page (`/{client}/files/scope`);
 *   Download PDF uses the stored library PDF.
 * - Other PDFs: authenticated in-app viewer (`/{client}/files/view/...`) with Download.
 * - Mobile Office docs / forceDownload: save to device.
 * - Other desktop types: blob preview tab when possible.
 *
 * @param {(fileId: string) => Promise<{ data: Blob, headers: any }>} fetchBlob
 * @param {{ forceDownload?: boolean }} [options] — when true, always save to device.
 */
export async function openStoredLibraryFile(file, client, fetchBlob, options = {}) {
  const id = (file?.id || "").trim();
  if (!id || typeof fetchBlob !== "function" || typeof window === "undefined") return false;

  const forceDownload = Boolean(options?.forceDownload);
  const packetPath = portalFilePacketPath(file, client);
  const viewPath = portalFileViewPath(file, client);
  const mobile = isMobilePdfViewer();

  // Scopes open as HTML documents (same feel as proposals / agreements).
  if (!forceDownload && packetPath && isScopeLibraryFile(file)) {
    return openAppPathInNewTab(packetPath);
  }

  // Prefer in-app PDF viewer (Download button uses the stored PDF as-is).
  if (!forceDownload && viewPath && isLikelyPdfLibraryFile(file)) {
    return openAppPathInNewTab(viewPath);
  }

  // Desktop only: reserve a tab during the click gesture (popup-blocker safe).
  // iOS Safari cannot navigate that tab to a blob: URL, so skip it on mobile.
  let previewTab = null;
  if (!mobile && !forceDownload) {
    previewTab = window.open("about:blank", "_blank");
    if (previewTab) {
      try {
        previewTab.document.title = "Opening…";
        previewTab.document.body.innerHTML =
          '<p style="font:14px/1.4 system-ui,sans-serif;color:#666;padding:24px">Opening file…</p>';
      } catch {
        // ignore
      }
      seedSessionTokenIntoWindow(previewTab);
      try {
        previewTab.opener = null;
      } catch {
        // ignore
      }
    }
  }

  const sameTabViewer = () => {
    if (!viewPath) return false;
    window.location.assign(new URL(viewPath, window.location.origin).href);
    return true;
  };

  try {
    const { data, headers } = await fetchBlob(id);
    if (await blobLooksLikeApiError(data)) {
      closeWindowQuietly(previewTab);
      return false;
    }
    const resolvedName = downloadFilenameFromHeaders(headers, uploadedFileName(file));
    const fileMeta = { name: resolvedName, original_filename: resolvedName };
    const mime = resolveUploadedContentType(
      fileMeta,
      typeof headers?.get === "function" ? headers.get("content-type") : headers?.["content-type"],
    );
    const pdf = looksLikePdf(fileMeta, mime);
    const blob = new Blob([data], {
      type: pdf ? "application/pdf" : mime || "application/octet-stream",
    });

    // After fetch: PDFs still prefer the in-app viewer when not force-downloading.
    if (pdf && !forceDownload && viewPath) {
      closeWindowQuietly(previewTab);
      return openAppPathInNewTab(viewPath);
    }

    // Client portal + Office docs + mobile forced downloads: save to device.
    if (forceDownload || mobile || shouldDownloadUploadedFile(fileMeta, mime)) {
      closeWindowQuietly(previewTab);
      triggerBlobDownload(blob, resolvedName, blob.type);
      return true;
    }

    // Desktop image/other: native preview in the reserved tab.
    const blobUrl = URL.createObjectURL(blob);
    if (previewTab && !previewTab.closed) {
      try {
        previewTab.location.href = blobUrl;
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 120_000);
        return true;
      } catch {
        // fall through to download
      }
    }
    URL.revokeObjectURL(blobUrl);
    closeWindowQuietly(previewTab);
    triggerBlobDownload(blob, resolvedName, blob.type);
    return true;
  } catch {
    closeWindowQuietly(previewTab);
    // Last resort: same-tab authenticated viewer (keeps session).
    if (viewPath) return sameTabViewer() || openAppPathInNewTab(viewPath);
    return false;
  }
}

export const blobLooksLikeApiError = async (blob) => {
  if (!(blob instanceof Blob)) return false;
  const type = (blob.type || "").toLowerCase();
  if (type.includes("json")) return true;
  if (blob.size > 4096) return false;
  const head = (await blob.slice(0, 200).text()).trim();
  return head.startsWith("{") || head.startsWith("[");
};

export const readApiErrorDetail = async (error) => {
  const direct = error?.response?.data?.detail;
  if (direct) return direct;
  if (error?.response?.data instanceof Blob) {
    try {
      const parsed = JSON.parse(await error.response.data.text());
      return parsed.detail;
    } catch {
      return null;
    }
  }
  return null;
};

/** True on phones/tablets where embedded PDF viewers need different open params. */
export function isMobilePdfViewer() {
  if (typeof window === "undefined") return false;
  const coarse = window.matchMedia?.("(pointer: coarse)")?.matches;
  const narrow = window.matchMedia?.("(max-width: 768px)")?.matches;
  const mobileUa = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");
  return Boolean(coarse || narrow || mobileUa);
}

/** Optional FitH helps desktop Chrome; on mobile it often hides pages after the first. */
export function pdfViewerSrc(url, { fitWidth = true } = {}) {
  if (!url) return url;
  const base = url.split("#")[0];
  return fitWidth ? `${base}#view=FitH` : base;
}
